<script setup lang="ts">
import { PlusCircleIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { useFormContext } from 'vee-validate'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import SingleSelect from '@/components/common/ui/inputs/SingleSelect.vue'
import { useEmployeeOptions } from '@/composables/useEmployeeOptions'
import { RelationType } from '@/constants/relations'
import { routeEmployeeCreate } from '@/routers/routes/mainRoutes/routeHR/employeeRoutes'
import { useCreateRelationSlideInStore } from '@/stores/createRelationSlideInStore'
import { useRatingStore } from '@/stores/ratingStore'
import { translationFunctionDefault } from '@/utilities/translations'

interface SectionMetaDataProps {
  onSubmit?: (() => void) | undefined
}

defineProps<SectionMetaDataProps>()

const tfDefault = translationFunctionDefault()

const { employeeOptions } = useEmployeeOptions()
const router = useRouter()

const ratingStore = useRatingStore()
const { fetchRatingsByDoctype } = ratingStore
const { ratingsOptions } = storeToRefs(ratingStore)

const employeeOptionsWithNoneOption = computed(() => {
  return [
    {
      isHideIcon: true,
      isPlaceholderOption: true,
      optionLabel: tfDefault('editRelationGroup.employeeSelectNoneOption'),
      optionValue: null,
    },
    ...employeeOptions.value,
  ]
})

onMounted(async () => {
  await fetchRatingsByDoctype(RelationType.customers)
})

const createRelationSlideInStore = useCreateRelationSlideInStore()
const { prefillMetaData } = storeToRefs(createRelationSlideInStore)

const { values } = useFormContext()

const ratingUserModified = ref(false)
const employeeUserModified = ref(false)

watch(
  () => values['tw_rating'],
  () => {
    if (prefillMetaData.value?.tw_rating) {
      ratingUserModified.value = true
    }
  },
)

watch(
  () => values['tw_responsible_employee'],
  () => {
    if (prefillMetaData.value?.tw_responsible_employee) {
      employeeUserModified.value = true
    }
  },
)

const prefillHintText = computed(() => {
  if (!prefillMetaData.value) return ''
  if (prefillMetaData.value.sourceEntityType === 'organization') {
    return tfDefault('createRelation.prefillHintFromOrganization', {
      name: prefillMetaData.value.sourceEntityName,
    })
  }
  return tfDefault('createRelation.prefillHintFromPerson', {
    name: prefillMetaData.value.sourceEntityName,
  })
})

const showRatingHint = computed(
  () => !!prefillMetaData.value?.tw_rating && !ratingUserModified.value,
)

const showEmployeeHint = computed(
  () => !!prefillMetaData.value?.tw_responsible_employee && !employeeUserModified.value,
)
</script>

<template>
  <section
    class="w-full space-y-5 border-b border-dotted border-black-200 px-8 py-7"
    :data-testid="TEST_IDS_CREATE.sectionMetaData.root"
  >
    <div class="space-y-1">
      <SingleSelect
        :data-testid="TEST_IDS_CREATE.sectionMetaData.rating"
        :label="tfDefault('editRelationGroup.ratingSelectLabel')"
        name="tw_rating"
        on-white-bg
        :options="[ratingsOptions]"
        :placeholder="tfDefault('editRelationGroup.selectPlaceholder')"
        @blur="onSubmit"
      />
      <p v-if="showRatingHint" class="text-xs text-black-400">{{ prefillHintText }}</p>
    </div>
    <div class="space-y-1">
      <SingleSelect
        :data-testid="TEST_IDS_CREATE.sectionMetaData.responsibleEmployee"
        :empty-state-action="{
          labelTranslationKey: 'common:addEntity.employee',
          icon: PlusCircleIcon,
          action: () => {
            router.push({ name: routeEmployeeCreate.name })
          },
        }"
        is-responsible-employee
        :label="tfDefault('editRelationGroup.employeeSelectLabel')"
        name="tw_responsible_employee"
        on-white-bg
        :options="[employeeOptionsWithNoneOption]"
        :placeholder="tfDefault('editRelationGroup.selectPlaceholder')"
        searchable
        show-avatars
        @blur="onSubmit"
      />
      <p v-if="showEmployeeHint" class="text-xs text-black-400">{{ prefillHintText }}</p>
    </div>
  </section>
</template>
